import React, { useMemo, useRef } from 'react';
import { GoogleMap, useJsApiLoader, OverlayView, Circle } from '@react-google-maps/api';
import { Helmet } from 'react-helmet';
import { Alert } from 'react-bootstrap';
import './MapComponent.css';

const mapContainerStyle = { width: '100%', height: '100%' };
const defaultCenter = { lat: 37.7749, lng: -122.4194 };
const LIBRARIES = ['places'];

const darkModeStyles = [
  { elementType: 'geometry', stylers: [{ color: '#18131f' }] },
  { elementType: 'labels.text.fill', stylers: [{ color: '#bcb3c8' }] },
  { elementType: 'labels.text.stroke', stylers: [{ color: '#18131f' }] },
  { featureType: 'administrative', elementType: 'geometry.stroke', stylers: [{ color: '#3a3043' }] },
  { featureType: 'landscape', elementType: 'geometry', stylers: [{ color: '#1f1927' }] },
  { featureType: 'road', elementType: 'geometry', stylers: [{ color: '#31283a' }] },
  { featureType: 'road.arterial', elementType: 'geometry', stylers: [{ color: '#3a3044' }] },
  { featureType: 'road.highway', elementType: 'geometry', stylers: [{ color: '#4a3b58' }] },
  { featureType: 'road.highway', elementType: 'geometry.stroke', stylers: [{ color: '#241c2c' }] },
  { featureType: 'water', elementType: 'geometry', stylers: [{ color: '#101521' }] },
  { featureType: 'poi', stylers: [{ visibility: 'off' }] },
  { featureType: 'poi.business', stylers: [{ visibility: 'off' }] },
  { featureType: 'transit', stylers: [{ visibility: 'off' }] },
];

export default function MapComponent({ markers = [], center, userLocation, accuracy, highlightedId, onMarkerClick, onMapLoad, onMapClick }) {
  const mapRef = useRef(null);
  const { isLoaded, loadError } = useJsApiLoader({
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY,
    libraries: LIBRARIES,
  });

  const safeCenter = useMemo(() => center || userLocation || defaultCenter, [center, userLocation]);

  if (loadError) return <Alert variant="danger">Map failed to load. Check the Google Maps configuration.</Alert>;
  if (!isLoaded) return <div className="p4r-map-loading">Loading map…</div>;

  return (
    <>
      <Helmet><title>Pool Map & Nearby Venues | Pool 4 Real</title></Helmet>
      <GoogleMap
        mapContainerStyle={mapContainerStyle}
        center={safeCenter}
        zoom={13}
        onLoad={(map) => { mapRef.current = map; onMapLoad?.(map); }}
        onClick={onMapClick}
        options={{
          streetViewControl: false,
          fullscreenControl: false,
          zoomControl: false,
          mapTypeControl: false,
          scaleControl: false,
          rotateControl: false,
          clickableIcons: false,
          gestureHandling: 'greedy',
          styles: darkModeStyles,
          backgroundColor: '#120d17',
        }}
      >
        {userLocation && accuracy && (
          <Circle
            center={userLocation}
            radius={Math.max(20, Math.min(accuracy, 250))}
            options={{
              fillColor: '#8b5cf6',
              fillOpacity: 0.1,
              strokeColor: '#c4b5fd',
              strokeOpacity: 0.42,
              strokeWeight: 1,
              clickable: false,
            }}
          />
        )}

        {userLocation && (
          <OverlayView position={userLocation} mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}>
            <div className="p4r-user-location-dot" title="Your location"><span /></div>
          </OverlayView>
        )}

        {markers.map((marker) => (
          <OverlayView
            key={marker.id}
            position={{ lat: marker.latitude, lng: marker.longitude }}
            mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
          >
            <button
              type="button"
              className={`marker-wrapper ${highlightedId === marker.id ? 'is-highlighted' : ''}`}
              onClick={(event) => { event.stopPropagation(); onMarkerClick?.(marker); }}
              aria-label={`Open ${marker.businessBrand || 'business'}`}
            >
              <span className="custom-marker">
                <span className="marker-ring" style={{ borderColor: getRingColor(marker.businessType) }} />
                <img className="marker-image" src={marker.profileImageUrl || '/default-avatar.png'} alt="" />
              </span>
              <span className="marker-title">{marker.businessBrand}</span>
            </button>
          </OverlayView>
        ))}
      </GoogleMap>
    </>
  );
}

function getRingColor(type) {
  switch (type) {
    case 'Retailer': return 'rgba(34,197,94,.88)';
    case 'Service': return 'rgba(251,146,60,.88)';
    case 'Manufacturer': return 'rgba(250,204,21,.88)';
    default: return 'rgba(167,139,250,.9)';
  }
}
